/**
 * NP-456 — the shared vocabulary for "your session ended, here is why".
 *
 * The Login page already had a session-expired banner, but the only producer was
 * ProtectedRoute, which flags expiry as a bare `true`. The inactivity logout in
 * SessionTimeout emitted no signal at all, so the banner never appeared for the
 * case users actually hit.
 *
 * Rather than add a second banner, the signal now carries a REASON so the one
 * banner can word itself correctly. Kept in a single module so the producers
 * (SessionTimeout, ProtectedRoute) and the consumer (Login) cannot drift apart
 * on the spelling of the value.
 */

/** Query-string / route-state key. Unchanged, so existing links keep working. */
export const SESSION_EXPIRED_PARAM = "sessionExpired";

/** Signal value for "idled out", as opposed to the pre-existing generic expiry. */
export const SESSION_EXPIRY_INACTIVITY = "inactivity";

export type SessionExpiryReason = "inactivity" | "generic";

/**
 * Normalises a raw signal — from route state OR the query string — into a reason.
 * Returns `null` when the session did not expire.
 *
 * Only the known vocabulary is accepted: `true` / `"true"` (ProtectedRoute's
 * existing signal, and any bookmarked `?sessionExpired=true` URL) map to the
 * generic reason, and `"inactivity"` to the idle-out one. Anything else is not a
 * signal we emit, so `/login?sessionExpired=foo` must NOT raise the banner —
 * treating any non-empty string as an expiry would widen the query-string
 * contract beyond the pre-NP-456 `sessionExpired === "true"` behaviour.
 */
export function parseSessionExpiry(raw: unknown): SessionExpiryReason | null {
  if (raw === true) return "generic";
  if (typeof raw !== "string") return null;

  const value = raw.trim().toLowerCase();
  if (value === SESSION_EXPIRY_INACTIVITY) return "inactivity";
  if (value === "true") return "generic";
  return null;
}
